import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useClaudeFleetService } from '@/services/claude-fleet'
import type {
  FleetOwnerThanks,
  FleetRunner,
  FleetRunnerActionRequest,
  FleetRunnerDetail,
  FleetRunnersTotals,
  FleetRunnerWindow
} from '@/services/claude-fleet'
import { fleetErrorText, sectionFailureText } from '@/stores/claude-fleet.store'
import type { MyRunnerFleet } from '@/services/my-runner/my-runner.types'
import { getErrorStatus } from '@/utils/error-handler'

const noTotals = (): FleetRunnersTotals => ({ live: 0, itemsToday: 0, savedUsdToday: 0 })

/**
 * The laptops engineers made fleet runners, for the Claude Fleet page's Runners panel. Kept apart from
 * the page's main store because the panel refreshes on its own cadence, as laptops come and go.
 */
export const useClaudeFleetRunnersStore = defineStore('adx-claude-fleet-runners', () => {
  const fleetService = useClaudeFleetService()

  const runners = ref<FleetRunner[]>([])
  const totals = ref<FleetRunnersTotals>(noTotals())
  /** Each engineer's laptop runs today and this week; empty from a fleet service older than runner health. */
  const owners = ref<FleetOwnerThanks[]>([])
  /** The defaults and kinds a new runner takes (My runner's), for "Make your computer a runner"; null until sent. */
  const defaults = ref<MyRunnerFleet | null>(null)
  /** Whether the viewer is on the Engineering Team page; null while unknown. */
  const viewerEngineer = ref<boolean | null>(null)
  /** True once the fleet has answered, so an empty list reads as "no laptops" rather than "not loaded". */
  const loaded = ref(false)
  const isLoading = ref(false)
  /** The last load failed, so the panel can offer a retry instead of looking empty. */
  const failed = ref(false)
  const failureText = ref<string | null>(null)
  const error = ref<string | null>(null)
  /** The machine whose menu action is on its way to the fleet; one at a time. */
  const actionMachine = ref<string | null>(null)

  /** The laptop whose drawer is open, and what the fleet said about it. */
  const detailMachine = ref<string | null>(null)
  const detail = ref<FleetRunnerDetail | null>(null)
  const detailLoading = ref(false)
  const detailFailed = ref(false)

  /** The open drawer's /runner window conversation, and why it shows none ("not uploaded yet"). */
  const windowMachine = ref<string | null>(null)
  const runnerWindow = ref<FleetRunnerWindow | null>(null)
  const windowUnavailable = ref<string | null>(null)
  /** A message to the window is on its way to the fleet. */
  const isSaying = ref(false)

  const hasError = computed(() => error.value !== null)
  /** Live laptop runners first, then the ones gone quiet in the last 24 hours. */
  const sortedRunners = computed(() =>
    [...runners.value].sort(
      (a, b) => Number(b.live) - Number(a.live) || b.lastSeen.localeCompare(a.lastSeen)
    )
  )

  // Id of the latest read, so a slow reply cannot overwrite a newer one.
  let activeRequestId = 0

  /** `background` is the panel's auto-refresh: no spinner, and a failure keeps the list shown without a toast. */
  async function fetchRunners({ background = false } = {}) {
    const requestId = ++activeRequestId
    if (!background) {
      isLoading.value = true
      error.value = null
      failed.value = false
    }
    try {
      const listed = await fleetService.listRunners()
      if (requestId !== activeRequestId) return
      runners.value = listed.runners
      totals.value = listed.totals
      owners.value = listed.owners ?? []
      defaults.value = listed.defaults ?? null
      viewerEngineer.value = listed.viewerEngineer ?? null
      loaded.value = true
      failed.value = false
    } catch (e) {
      if (requestId !== activeRequestId || background) return
      failed.value = true
      failureText.value = sectionFailureText(e)
      // A 403 is the page's access state, which the main fleet store raises from the same refusal.
      if (getErrorStatus(e) !== 403) error.value = fleetErrorText(e, 'Failed to load the runners')
    } finally {
      if (requestId === activeRequestId) isLoading.value = false
    }
  }

  /** Sends a menu action, then re-reads the runners so the row shows it pending. Throws nothing: a refusal
   *  is the page's error toast. Resolves true when the fleet accepted it. */
  async function sendAction(machine: string, request: FleetRunnerActionRequest): Promise<boolean> {
    actionMachine.value = machine
    error.value = null
    try {
      await fleetService.sendRunnerAction(machine, request)
      await fetchRunners({ background: true })
      if (detailMachine.value === machine) await fetchRunnerDetail(machine, { background: true })
      return true
    } catch (e) {
      error.value = fleetErrorText(e, 'The runner action was not sent')
      return false
    } finally {
      actionMachine.value = null
    }
  }

  // Id of the latest drawer read, so a slow reply for a laptop the drawer moved off cannot overwrite it.
  let activeDetailRequestId = 0

  /** Reads one laptop for its drawer; `background` refreshes an open drawer without blanking it. */
  async function fetchRunnerDetail(machine: string, { background = false } = {}) {
    const requestId = ++activeDetailRequestId
    if (detailMachine.value !== machine) detail.value = null
    detailMachine.value = machine
    if (!background) {
      detailLoading.value = true
      detailFailed.value = false
    }
    try {
      const read = await fleetService.getRunner(machine)
      if (requestId !== activeDetailRequestId) return
      detail.value = read
      detailFailed.value = false
    } catch {
      if (requestId !== activeDetailRequestId || background) return
      detailFailed.value = true
    } finally {
      if (requestId === activeDetailRequestId) detailLoading.value = false
    }
  }

  function closeRunnerDetail() {
    activeDetailRequestId++
    detailMachine.value = null
    detail.value = null
    detailLoading.value = false
    detailFailed.value = false
    clearRunnerWindow()
  }

  // Id of the latest window read, so a slow reply for a laptop the drawer moved off cannot overwrite it.
  let activeWindowRequestId = 0

  /** Reads the /runner window's conversation; a 404 (nothing uploaded yet) or 403 is shown in place, never toasted. */
  async function fetchRunnerWindow(machine: string) {
    const requestId = ++activeWindowRequestId
    if (windowMachine.value !== machine) {
      runnerWindow.value = null
      windowUnavailable.value = null
    }
    windowMachine.value = machine
    try {
      const read = await fleetService.getRunnerWindow(machine)
      if (requestId !== activeWindowRequestId) return
      runnerWindow.value = read
      windowUnavailable.value = null
    } catch (e) {
      if (requestId !== activeWindowRequestId) return
      const status = getErrorStatus(e)
      if (status === 404) windowUnavailable.value = 'The window has not uploaded its conversation yet. It does within a minute of opening.'
      else if (status === 403) windowUnavailable.value = "Only this runner's owner and fleet operators can read its window."
      else if (!runnerWindow.value) windowUnavailable.value = fleetErrorText(e, 'Could not read the window conversation')
    }
  }

  function clearRunnerWindow() {
    activeWindowRequestId++
    windowMachine.value = null
    runnerWindow.value = null
    windowUnavailable.value = null
  }

  /**
   * Sends a message to the machine's /runner window, then reads its conversation again. Resolves true when sent. The
   * fleet names the sender from the signed-in person, so the window never reads a name the browser chose.
   */
  async function sayToWindow(machine: string, text: string): Promise<boolean> {
    isSaying.value = true
    try {
      const sent = await sendAction(machine, { action: 'say', text })
      if (sent && windowMachine.value === machine) await fetchRunnerWindow(machine)
      return sent
    } finally {
      isSaying.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    activeRequestId++
    runners.value = []
    totals.value = noTotals()
    owners.value = []
    defaults.value = null
    viewerEngineer.value = null
    loaded.value = false
    isLoading.value = false
    failed.value = false
    error.value = null
    actionMachine.value = null
    closeRunnerDetail()
  }

  return {
    runners,
    defaults,
    viewerEngineer,
    totals,
    owners,
    loaded,
    isLoading,
    failed,
    failureText,
    error,
    hasError,
    sortedRunners,
    actionMachine,
    detailMachine,
    detail,
    detailLoading,
    detailFailed,
    windowMachine,
    runnerWindow,
    windowUnavailable,
    isSaying,
    fetchRunners,
    fetchRunnerWindow,
    clearRunnerWindow,
    sayToWindow,
    fetchRunnerDetail,
    closeRunnerDetail,
    sendAction,
    clearError,
    reset
  }
})
